import styled from '@emotion/styled';
import { ButtonProps } from '@fluentui/react-components';
import {
  Checkbox,
  Option as GalaxyOption,
  OptionGroup,
  Popover,
  PopoverProps,
  PopoverSurface,
  PopoverTrigger,
  SearchDefault16Px,
  TextInput,
  grey,
} from '@galaxy-react/core';
import React, { CSSProperties, useMemo, useState } from 'react';
import MenuButtonStyled from '../MenuButton';
import Option from './Option';

type Props<T> = Omit<PopoverProps, 'appearance' | 'children'> & {
  options: { label: string; value: T }[];
  selectedOptions?: T[];
  multiselect?: boolean;
  value?: React.ReactNode;
  trigger?: React.ReactElement;
  onChange: (payload: { optionText: string; optionValue: T; selectedOptions: T[] }) => void;
  appearance?: ButtonProps['appearance'];
  style?: CSSProperties;
  disabled?: boolean;
  menuIcon?: any;
  triggerIcon?: any;
  isTopLevel?: boolean;
  showSearch?: boolean;
  placeholder?: string;
  onClick?: () => void;
  labelRenderer?: (label: string, item?: any) => React.ReactNode;
};
const PopoverSurfaceStyled = styled(PopoverSurface)<{
  isTopLevel?: boolean;
}>(({ isTopLevel }) => ({
  '&.fui-PopoverSurface': {
    background: '#fff',
    padding: '2px 0 4px',
    animationDuration: '0s',
    maxWidth: '600px',
    minWidth: '250px',
    top: isTopLevel ? '4px !important' : '0px', // Hard override of menu popover inline style
  },
}));

const OptionGroupStyled = styled(OptionGroup)({
  '&.fui-OptionGroup': {
    '&:not(:last-child)::after': {
      paddingBottom: '0',
      margin: '0',
    },
    '&:not(:first-child)::after': {
      paddingTop: '0',
    },
  },
});

export const NoResultsStyled = styled(GalaxyOption)({
  '&.fui-Option': {
    color: grey[500],
    padding: '10px 16px',
    margin: '2px 0 0',
    '&:active, &:focused, &:hover': {
      color: grey[500],
    },
    '&:hover:active': {
      color: grey[500],
    },
  },
});

const Dropdown = <T extends React.Key>(props: Props<T>, ref) => {
  const {
    options,
    disabled,
    value,
    multiselect,
    appearance,
    selectedOptions,
    style,
    onChange,
    menuIcon,
    onClick,
    isTopLevel,
    trigger,
    triggerIcon,
    showSearch = true,
    labelRenderer,
    placeholder = 'Please select',
    ...restProps
  } = props;
  const [searchText, setSearchText] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  const filteredOptions = useMemo(() => {
    return options?.filter((option) => {
      return option.label?.toLowerCase()?.replace(/\s/g, '').includes(searchText?.toLowerCase()?.replace(/\s/g, ''));
    });
  }, [options, searchText]);

  const onChangeSelectAll = () => {
    if (selectedOptions?.length !== options.length) {
      onChange?.({
        optionText: undefined,
        optionValue: undefined,
        selectedOptions: options.map((item) => item.value),
      });
    } else {
      onChange?.({ optionText: undefined, optionValue: undefined, selectedOptions: [] });
    }
  };

  const handleSelect = ({ label, value }) => {
    if (multiselect) {
      const newSelectedOptions = selectedOptions?.includes(value)
        ? selectedOptions?.filter((item) => item !== value)
        : selectedOptions?.concat(value);
      onChange?.({ optionText: label, optionValue: value, selectedOptions: [...newSelectedOptions] });
    } else {
      onChange?.({ optionText: label, optionValue: value, selectedOptions: [value] });
      setOpen(false);
    }
  };

  return (
    <Popover
      positioning="below-start"
      open={open}
      onOpenChange={(e, data) => {
        setOpen(data.open);
      }}
      {...restProps}
    >
      <PopoverTrigger disableButtonEnhancement>
        {trigger ? (
          trigger
        ) : (
          <MenuButtonStyled
            ref={ref}
            style={style}
            icon={triggerIcon}
            menuIcon={menuIcon}
            disabled={disabled}
            placeholder={placeholder}
            appearance={appearance}
            onClick={() => onClick?.()}
          >
            {value}
          </MenuButtonStyled>
        )}
      </PopoverTrigger>

      <PopoverSurfaceStyled tabIndex={-1} isTopLevel={isTopLevel}>
        {options?.length > 5 && showSearch && (
          <div style={{ padding: '2px 5px' }}>
            <TextInput
              style={{ width: '100%' }}
              placeholder="Search"
              aria-label="Search"
              contentBefore={<SearchDefault16Px />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        )}

        {multiselect && options.length > 0 && (
          <OptionGroupStyled>
            <Option
              style={{ flexShrink: 0, margin: '2px 5px 0' }}
              onClick={onChangeSelectAll}
              multiSelect={multiselect}
              checkIcon={
                multiselect && (
                  <Checkbox
                    tabIndex={-1}
                    checked={
                      selectedOptions?.length > 0 && selectedOptions?.length !== options?.length
                        ? 'mixed'
                        : !!selectedOptions?.length
                    }
                  />
                )
              }
            >
              {searchText
                ? `${selectedOptions?.length ?? 0} selected`
                : `${selectedOptions?.length ?? 0} of ${options?.length} selected`}
            </Option>
          </OptionGroupStyled>
        )}
        <OptionGroup style={{ maxHeight: '40vh', overflowY: 'auto', gap: 4, padding: '4px 5px 0' }}>
          {filteredOptions.map((option) => (
            <Option
              style={{ flexShrink: 0 }}
              key={option.value}
              onClick={() => handleSelect(option)}
              isSelected={selectedOptions?.includes(option.value)}
              multiSelect={multiselect}
              checkIcon={multiselect && <Checkbox checked={selectedOptions?.includes(option.value)} />}
            >
              {labelRenderer ? labelRenderer(option.label, option) : option.label}
            </Option>
          ))}
          {!filteredOptions.length && (
            <NoResultsStyled>{searchText ? `No options for ${searchText}` : 'No options'}</NoResultsStyled>
          )}
        </OptionGroup>
      </PopoverSurfaceStyled>
    </Popover>
  );
};

export default React.forwardRef(Dropdown) as <T extends React.Key>(
  props: Props<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
